import type {
  Notification,
  NotificationListParams,
  NotificationsResponse,
  Project,
} from "@facade/contracts";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import { notificationQueryKeys } from "../queries";
import { projectQueryKeys } from "@facade/features/projects/queries";

export type NotificationListCache = InfiniteData<
  NotificationsResponse,
  string | null
>;

export function notificationMatchesList(
  notification: Notification,
  params: Omit<NotificationListParams, "cursor">,
) {
  if (params.projectId && notification.projectId !== params.projectId) {
    return false;
  }
  if (params.unreadOnly && notification.readAt) {
    return false;
  }
  return true;
}

export function updateNotificationLists(
  queryClient: QueryClient,
  update: (notification: Notification) => Notification,
) {
  queryClient.setQueriesData<NotificationListCache>(
    { queryKey: notificationQueryKeys.lists() },
    (current) => {
      if (!current) return current;

      return {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          notifications: page.notifications.map(update),
        })),
      };
    },
  );
}

export function adjustProjectUnreadNotificationCount(
  queryClient: QueryClient,
  projectId: string,
  delta: number,
) {
  queryClient.setQueryData<Project[]>(projectQueryKeys.all, (current) =>
    current?.map((project) =>
      project.id === projectId
        ? {
            ...project,
            unreadNotificationCount: Math.max(
              0,
              (project.unreadNotificationCount ?? 0) + delta,
            ),
          }
        : project,
    ),
  );
}

export function clearProjectUnreadNotificationCounts(
  queryClient: QueryClient,
) {
  queryClient.setQueryData<Project[]>(projectQueryKeys.all, (current) =>
    current?.map((project) => ({
      ...project,
      unreadNotificationCount: 0,
    })),
  );
}
